import { useNavigate } from 'react-router-dom'
import { EligibleFund } from './EligibleFund'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { getAvailableLabelFromHarvestingType } from '../../utils/misc'
import { HarvestingPlanType } from '../../types/summary.types'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { LockIcon } from '../LockIcon'
import type { HarvestingPlanFund } from './PlanFund'
import BottomSheetContent from '../../../../components/BottomSheetComponents/BottomSheetContent'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { SecondaryButton } from '../../../../components/execution/SecondaryButton'
import { HowGainsHarvestingWorksSheet } from '../../bottom-sheets/HowGainsHarvestingWorksSheet'
import { PrimaryButton } from '../../../../components/execution/PrimaryButton'
import { getEliteCTA } from '../../../../utils/mutualFundDetail'
import { postCTA } from '../../../../utils/appBridge'
import {
  TaxHarvestingScreenName,
  TaxHarvestingEventId,
  TaxHarvestingButtonLabel,
  trackTaxHarvestingUserClicked,
  trackTaxHarvestingButtonTap
} from '../../tracking/taxHarvestingTracking'
import { planMockFunds } from '../../mock/data/planMockFunds'
import type { TaxHarvestingPlanFundDetail } from '../../api/planDetailApi'

const HarvestSVG = ({ size = 18 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 18 18" fill="none">
    <path
      d="M5.45142 12.4351L6.45391 11.4326C7.79057 10.0959 7.79057 7.92879 6.45391 6.59212L6.3071 6.44531"
      stroke="#992E00"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.1236 7.67017C13.3465 7.42932 13.5165 7.12722 13.6054 6.7717L14.1097 4.75487C14.2576 4.16301 13.7216 3.6269 13.1297 3.77486L11.1128 4.27908C10.7573 4.36795 10.4552 4.538 10.2144 4.76093"
      stroke="#992E00"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.02382 12.8578L4.87701 12.711C3.54035 11.3744 3.54035 9.2072 4.87701 7.87054L9.30224 3.44531L9.44905 3.59212C10.7857 4.92879 10.7857 7.09594 9.44905 8.4326L5.02382 12.8578Z"
      stroke="#992E00"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.17147 13.0042L5.02466 12.8574L9.44989 8.43218C10.7865 7.09552 12.9537 7.09553 14.2903 8.43218L14.4372 8.579L10.0119 13.0042C8.67529 14.3409 6.50813 14.3409 5.17147 13.0042Z"
      stroke="#992E00"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.45093 12.4347L6.45342 11.4322C7.79008 10.0955 9.95724 10.0955 11.2939 11.4322L11.4407 11.579"
      stroke="#992E00"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.31152 14.5749L5.87857 12.0078"
      stroke="#992E00"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)


type GainsHarvestingPlanProps = {
  /** When true, blur the plan and show Elite unlock overlay */
  showEliteLockOverlay?: boolean
  /** Total funds count from plan detail response (used for eligible funds label) */
  totalFundCount?: number
  /** Backend-driven eligible plan details */
  eligiblePlanDetails?: TaxHarvestingPlanFundDetail[]
  /** Additional funds only shown when user taps "View all eligible funds" */
  remainingPlanDetails?: TaxHarvestingPlanFundDetail[]
  /** Harvest type from query params (e.g. EQ_LTCG) */
  harvestType?: string | null
  /** Possible harvesting types for the current plan (from summary); sent to recommendation as comma-separated */
  possibleFolioTaxHarvestingTypes?: HarvestingPlanType[]
  /** From summary API: possible tax save for this gains plan (LTCG) */
  summaryPossibleTaxSave?: number
  /** From summary API: tax-free gains amount for this gains plan (LTCG) */
  summaryTaxFreeBookAmount?: number
  /** When true, render inside bottom sheet: all funds, no "View all" button; requires onDismiss */
  isInBottomSheet?: boolean
  /** Required when isInBottomSheet; called when sheet is dismissed */
  onDismiss?: () => void
}

const FUNDS_PREVIEW_COUNT = 3

function GainsHarvestingPlanContent({
  showEliteLockOverlay,
  onHowGainsHarvestingWorksClick,
  fundsForDisplay,
  eligibleFundsCount,
  harvestType,
  possibleFolioTaxHarvestingTypes,
  isInBottomSheet,
  onViewAllClick,
  formatCurrency,
  totalAvailableLtcg,
  summaryPossibleTaxSave,
  summaryTaxFreeBookAmount,
  hasRemainingFunds,
  onBottomSheetDismiss
}: {
  showEliteLockOverlay: boolean
  onHowGainsHarvestingWorksClick: () => void
  fundsForDisplay: HarvestingPlanFund[]
  eligibleFundsCount: number
  harvestType: string | null | undefined
  possibleFolioTaxHarvestingTypes?: HarvestingPlanType[]
  isInBottomSheet: boolean
  onViewAllClick?: () => void
  formatCurrency: (value: number) => string
  totalAvailableLtcg: number
  summaryPossibleTaxSave?: number
  summaryTaxFreeBookAmount?: number
  hasRemainingFunds: boolean
  onBottomSheetDismiss?: () => void
}) {
  const navigate = useNavigate()
  const fundsToShow = fundsForDisplay
  const showViewAllButton = !showEliteLockOverlay && !isInBottomSheet && hasRemainingFunds

  const displayPossibleTaxSave = summaryPossibleTaxSave ?? 0
  const displayTaxFreeBookAmount = summaryTaxFreeBookAmount ?? totalAvailableLtcg

  return (
    <>
      <div className='flex flex-row items-center gap-1 font-medium px-5'>
        <span>
          <HarvestSVG />
        </span>
        <span className='text-sm text-[#992E00]'>Gains Harvesting Plan</span>
      </div>

      <div className='mt-3 text-[22px] leading-[1.3] px-5 text-grey40'>
        <span className='font-medium text-black'>Save {formatCurrency(displayPossibleTaxSave)} in future tax</span> by booking{' '}
        {formatCurrency(displayTaxFreeBookAmount)} gains from these funds
      </div>

      <div
        onClick={onHowGainsHarvestingWorksClick}
        className='mt-3 text-[13px] text-grey40 underline cursor-pointer px-5'
      >
        How does booking gains save future tax?
      </div>

      <div className={showEliteLockOverlay ? 'blur-lg pointer-events-none select-none' : ''}>
        <div
          className={`mt-6 p-5 border-t border-b border-grey80 flex items-center justify-between text-[13px] text-[#6A6E6E] 
            ${isInBottomSheet ? 'sticky top-0 z-10 bg-white' : ''}`}
        >
          <span>{eligibleFundsCount} eligible funds</span>
          <span></span>
        </div>

        <div className='mt-4 flex flex-col gap-3'>
          {fundsToShow.map((fund, idx) => (
            <EligibleFund
              key={fund.isin}
              fundLogo={fund.fundLogo}
              fundTitle={fund.fundName}
              fundNature={fund.fundNature}
              fundCategory={fund.fundCategory}
              amount={fund.bookGain}
              powerRank={fund.rank}
              powerRating={fund.fundPerformance ?? undefined}
              isRegular={false}
              saveTaxAmount={fund.saveTax}
              isFutureTax={true}
              isGains={true}
              isLast={idx === fundsToShow.length - 1}
              onClick={() => {
                trackTaxHarvestingUserClicked({
                  screen: TaxHarvestingScreenName.Plan,
                  eventId: TaxHarvestingEventId.GainsFundCard,
                  fundIsin: fund.isin
                })
                onBottomSheetDismiss?.()
                const base = `/tax-harvesting/recommendation?isin=${fund.isin}&harvestType=${harvestType ?? ''}&amount=${fund.bookGain}&saveTax=${fund.saveTax}`
                const possibleParam =
                  Array.isArray(possibleFolioTaxHarvestingTypes) && possibleFolioTaxHarvestingTypes.length > 0
                    ? `&possibleFolioTaxHarvestingTypes=${possibleFolioTaxHarvestingTypes.join(',')}`
                    : ''
                navigate(`${base}${possibleParam}`)
              }}
            />
          ))}
        </div>

        {showViewAllButton && onViewAllClick && (
          <div className='w-full px-5 mt-6 mb-2'>
            <SecondaryButton onClick={onViewAllClick} label='View all eligible funds' />
          </div>
        )}
      </div>
    </>
  )
}

export default function GainsHarvestingPlan({
  showEliteLockOverlay = false,
  totalFundCount,
  eligiblePlanDetails,
  remainingPlanDetails,
  harvestType,
  possibleFolioTaxHarvestingTypes,
  summaryPossibleTaxSave,
  summaryTaxFreeBookAmount,
  isInBottomSheet = false,
  onDismiss
}: GainsHarvestingPlanProps) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const eligibleFunds: HarvestingPlanFund[] = Array.isArray(eligiblePlanDetails)
    ? eligiblePlanDetails
    : []
  const remainingFunds: HarvestingPlanFund[] = Array.isArray(remainingPlanDetails)
    ? remainingPlanDetails
    : []
  const hasRemainingFunds = remainingFunds.length > 0
  const allFunds: HarvestingPlanFund[] = [...eligibleFunds, ...remainingFunds]
  const baseFundsForPage: HarvestingPlanFund[] = eligibleFunds.length > 0 ? eligibleFunds : allFunds

  const effectiveFundsForDisplay: HarvestingPlanFund[] = showEliteLockOverlay
    ? planMockFunds
    : isInBottomSheet
      ? allFunds
      : baseFundsForPage

  if (effectiveFundsForDisplay.length === 0) {
    return null
  }

  const totalAvailableLtcg = allFunds.reduce((sum, f) => sum + f.bookGain, 0)
  const formatCurrency = (value: number) => `${roundOffAndFormatNumberWithCurrency(value)}`

  const handleHowGainsHarvestingWorksClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Plan,
      eventId: TaxHarvestingEventId.HowGainsHarvestingWorks
    })
    openBottomSheet(
      <HowGainsHarvestingWorksSheet onDismiss={closeBottomSheet} saveTax={summaryPossibleTaxSave} />,
      closeBottomSheet
    )
  }

  const handleViewAllClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Plan,
      eventId: TaxHarvestingEventId.ViewAllGainsFunds
    })
    openBottomSheet(
      <GainsHarvestingPlan
        showEliteLockOverlay={false}
        totalFundCount={totalFundCount}
        eligiblePlanDetails={eligiblePlanDetails}
        remainingPlanDetails={remainingPlanDetails}
        harvestType={harvestType}
        possibleFolioTaxHarvestingTypes={possibleFolioTaxHarvestingTypes}
        summaryPossibleTaxSave={summaryPossibleTaxSave}
        summaryTaxFreeBookAmount={summaryTaxFreeBookAmount}
        isInBottomSheet
        onDismiss={closeBottomSheet}
      />,
      closeBottomSheet
    )
  }

  const content = (
    <GainsHarvestingPlanContent
      showEliteLockOverlay={showEliteLockOverlay}
      onHowGainsHarvestingWorksClick={handleHowGainsHarvestingWorksClick}
      fundsForDisplay={effectiveFundsForDisplay}
      eligibleFundsCount={totalFundCount ?? allFunds.length}
      harvestType={harvestType}
      possibleFolioTaxHarvestingTypes={possibleFolioTaxHarvestingTypes}
      isInBottomSheet={isInBottomSheet}
      onViewAllClick={handleViewAllClick}
      formatCurrency={formatCurrency}
      totalAvailableLtcg={totalAvailableLtcg}
      summaryPossibleTaxSave={summaryPossibleTaxSave}
      summaryTaxFreeBookAmount={summaryTaxFreeBookAmount}
      hasRemainingFunds={hasRemainingFunds}
      onBottomSheetDismiss={closeBottomSheet}
    />
  )

  if (isInBottomSheet && onDismiss) {
    return (
      <BottomSheetContent heading='' bottomSheetHeadClassName='!pt-2' bottomSheetBodyClassName='!px-0 !overflow-visible' onDismiss={onDismiss}>
        <div className='py-4'>{content}</div>
      </BottomSheetContent>
    )
  }

  return (
    <div className='py-8 relative'>
      <div>{content}</div>
      {showEliteLockOverlay ? <GainsHarvestingPlanLockOverlay /> : null}
    </div>
  )
}

export const GainsHarvestingPlanLockOverlay = () => {
  return (
    <div className='absolute inset-0 z-10 flex items-center justify-center px-8 pointer-events-none'>
      <div className='flex flex-col items-center gap-4 text-center max-w-[286px] pointer-events-auto'>
        <RenderHTML innerHTMLText={LockIcon} />
        <p className='text-[16px] font-[400] leading-[150%] text-[#232323]'>
          See which funds to sell and exactly how much to book these gains
        </p>
        <p className='text-[13px] text-[#CC5C2D] leading-[150%] font-[400]'>
          Avail tax benefits before it expires on 30 March
        </p>
        <div className='pt-1'>
          <PrimaryButton
            onClick={() => {
              trackTaxHarvestingButtonTap({
                screen: TaxHarvestingScreenName.Plan,
                eventId: TaxHarvestingEventId.UnlockEliteOverlay,
                buttonLabel: TaxHarvestingButtonLabel.UnlockPlanWithElite
              })
              postCTA(JSON.stringify(getEliteCTA()))
            }}
            label='Unlock plan with Elite'
            primaryButtonClassName='text-[14px]'
          />
        </div>
      </div>
    </div>
  )
}
